/**
 * calculateMirrorWallSpace — determines the wall space available above a
 * vanity for mirrors and lights (height + left/right width + opening
 * obstructions).
 *
 * Moved from functions/features/tools/designAutomation/utils.js as Stage 1
 * of the vanity-accessory unification plan
 * (claude-docs/2026-04-20-unify-vanity-accessory-code-paths.md).
 * Behavior unchanged; re-exported from utils.js for backward compatibility.
 */

const { INCHES_TO_METERS } = require('./constants');
const { calculateAvailableWallSpace, getFloorY } = require('./placementUtils');
const { projectOntoWallAxis } = require('./wallFixtureCollision');
const { hydrateRoomOpenings } = require('../openings/openingSync');

/**
 * Calculate the wall space available for mirrors and lights above a vanity.
 * @param {Object} vanityCenter - Center position of the vanity {x, y, z}
 * @param {number} vanityWidth - Width of the vanity in meters
 * @param {number} countertopTopY - Y position of the top of the countertop
 * @param {Object} parentWall - The wall the vanity is against
 * @param {Object} roomData - Room data containing walls, objects, ceilings
 * @param {Object} [structureOpenings] - Optional `{doors, windows, openings}`
 *   from the structure-level canonical arrays. When provided, room openings
 *   are hydrated before the opening-overlap scan below — required for slim
 *   room copies post-PR #634, otherwise the `!opening.center || !opening.dimensions`
 *   guard silently skips every opening and mirrors get placed over doors / windows.
 * @returns {Object} Mirror wall space info
 */
function calculateMirrorWallSpace(
  vanityCenter,
  vanityWidth,
  countertopTopY,
  parentWall,
  roomData,
  structureOpenings = null
) {
  const effectiveRoomData = structureOpenings
    ? hydrateRoomOpenings(roomData, structureOpenings)
    : roomData;
  const ceilings = effectiveRoomData.ceilings || [];

  const MIN_REASONABLE_CEILING = 1.8;
  const DEFAULT_CEILING_HEIGHT = 2.44;
  let ceilingHeight = DEFAULT_CEILING_HEIGHT;

  if (parentWall) {
    const parentWallHeight = parentWall.dimensions?.y || parentWall.height || 0;
    if (parentWallHeight >= MIN_REASONABLE_CEILING) {
      ceilingHeight = parentWallHeight;
      console.log(
        `[MirrorWallSpace] Using parent wall height ${parentWallHeight.toFixed(2)}m from wall ${parentWall.id}`
      );
    } else if (parentWallHeight > 0) {
      console.log(
        `[MirrorWallSpace] WARNING: Parent wall height=${parentWallHeight.toFixed(2)}m seems too low, using default ${DEFAULT_CEILING_HEIGHT}m`
      );
    }
  } else if (ceilings.length > 0 && ceilings[0].center?.y) {
    const ceilingY = ceilings[0].center.y;
    if (ceilingY >= MIN_REASONABLE_CEILING) {
      ceilingHeight = ceilingY;
    } else {
      console.log(
        `[MirrorWallSpace] WARNING: Ceiling center.y=${ceilingY.toFixed(2)}m seems too low, using default ${DEFAULT_CEILING_HEIGHT}m`
      );
    }
  }

  const floorY = getFloorY(effectiveRoomData);
  const ceilingY = floorY + ceilingHeight;
  const availableHeight = ceilingY - countertopTopY;

  const horizontalSpace = calculateAvailableWallSpace(
    vanityCenter,
    vanityWidth,
    parentWall,
    effectiveRoomData
  );

  const UNBOUNDED_THRESHOLD = 2.0;

  let leftSpace = horizontalSpace.leftSpace;
  let rightSpace = horizontalSpace.rightSpace;
  let leftObstruction = horizontalSpace.leftObstruction;
  let rightObstruction = horizontalSpace.rightObstruction;

  // Check openings (doors, windows, openings) that overlap the mirror Y range.
  const mirrorYRange = { bottom: countertopTopY, top: ceilingY };
  const openingMargin = 0.0508; // 2 inches
  const allOpenings = [
    ...(effectiveRoomData.doors || []),
    ...(effectiveRoomData.windows || []),
    ...(effectiveRoomData.openings || []),
  ];
  const openingObstructions = [];

  if (parentWall) {
    const vanityProj = projectOntoWallAxis(vanityCenter, parentWall);
    if (vanityProj) {
      const vanityAlong = vanityProj.t * vanityProj.wallLength;

      for (const opening of allOpenings) {
        if (opening.parentWallIds?.[0] !== parentWall.id) continue;
        if (!opening.center || !opening.dimensions) continue;

        // Check vertical overlap with mirror zone
        const openingBottom =
          opening.center.y - (opening.dimensions.y || 1.0) / 2;
        const openingTop = opening.center.y + (opening.dimensions.y || 1.0) / 2;
        if (
          openingBottom >= mirrorYRange.top ||
          openingTop <= mirrorYRange.bottom
        )
          continue;

        // Project opening onto wall axis
        const openingProj = projectOntoWallAxis(opening.center, parentWall);
        if (!openingProj) continue;

        const openingAlong = openingProj.t * openingProj.wallLength;
        const openingHalfW = (opening.dimensions.x || 0.9) / 2;
        const openingLeft = openingAlong - openingHalfW - openingMargin;
        const openingRight = openingAlong + openingHalfW + openingMargin;

        openingObstructions.push({
          type: opening.type || 'opening',
          id: opening.id,
          left: openingLeft,
          right: openingRight,
        });

        // Narrow leftSpace/rightSpace if the opening intrudes
        const vanityLeftEdge = vanityAlong - vanityWidth / 2;
        const vanityRightEdge = vanityAlong + vanityWidth / 2;

        // Opening is to the left of vanity center and intrudes into leftSpace
        if (openingRight > vanityLeftEdge && openingAlong < vanityAlong) {
          const intrusionFromLeft = openingRight - vanityLeftEdge;
          if (
            intrusionFromLeft > 0 &&
            intrusionFromLeft < leftSpace + vanityWidth / 2
          ) {
            const newLeftSpace = Math.max(0, leftSpace - intrusionFromLeft);
            if (newLeftSpace < leftSpace) {
              console.log(
                `[MirrorWallSpace] Opening ${opening.type} ${opening.id} narrows left space: ${(leftSpace / INCHES_TO_METERS).toFixed(1)}" → ${(newLeftSpace / INCHES_TO_METERS).toFixed(1)}"`
              );
              leftSpace = newLeftSpace;
              leftObstruction = {
                type: opening.type || 'opening',
                id: opening.id,
              };
            }
          }
        }

        // Opening is to the right of vanity center and intrudes into rightSpace
        if (openingLeft < vanityRightEdge && openingAlong > vanityAlong) {
          const intrusionFromRight = vanityRightEdge - openingLeft;
          if (
            intrusionFromRight > 0 &&
            intrusionFromRight < rightSpace + vanityWidth / 2
          ) {
            const newRightSpace = Math.max(0, rightSpace - intrusionFromRight);
            if (newRightSpace < rightSpace) {
              console.log(
                `[MirrorWallSpace] Opening ${opening.type} ${opening.id} narrows right space: ${(rightSpace / INCHES_TO_METERS).toFixed(1)}" → ${(newRightSpace / INCHES_TO_METERS).toFixed(1)}"`
              );
              rightSpace = newRightSpace;
              rightObstruction = {
                type: opening.type || 'opening',
                id: opening.id,
              };
            }
          }
        }
      }
    }
  }

  const leftUnbounded =
    horizontalSpace.leftObstruction.type === 'wallEndpoint' &&
    leftSpace > UNBOUNDED_THRESHOLD;

  const rightUnbounded =
    horizontalSpace.rightObstruction.type === 'wallEndpoint' &&
    rightSpace > UNBOUNDED_THRESHOLD;

  let availableWidth = null;
  if (!leftUnbounded && !rightUnbounded) {
    availableWidth = leftSpace + vanityWidth + rightSpace;
  }

  console.log(`[MirrorWallSpace] Ceiling height: ${ceilingHeight.toFixed(2)}m`);
  console.log(
    `[MirrorWallSpace] Countertop top Y: ${countertopTopY.toFixed(2)}m`
  );
  console.log(
    `[MirrorWallSpace] Available height: ${availableHeight.toFixed(2)}m (${(availableHeight / INCHES_TO_METERS).toFixed(1)}")`
  );
  console.log(
    `[MirrorWallSpace] Left space: ${leftUnbounded ? 'unbounded' : `${(leftSpace / INCHES_TO_METERS).toFixed(1)}"`}, obstruction: ${leftObstruction.type}`
  );
  console.log(
    `[MirrorWallSpace] Right space: ${rightUnbounded ? 'unbounded' : `${(rightSpace / INCHES_TO_METERS).toFixed(1)}"`}, obstruction: ${rightObstruction.type}`
  );
  if (availableWidth !== null) {
    console.log(
      `[MirrorWallSpace] Total available width: ${(availableWidth / INCHES_TO_METERS).toFixed(1)}"`
    );
  } else {
    console.log(`[MirrorWallSpace] Total available width: unbounded`);
  }
  if (openingObstructions.length > 0) {
    console.log(
      `[MirrorWallSpace] ${openingObstructions.length} opening(s) overlap mirror zone`
    );
  }

  return {
    availableHeight,
    availableWidth,
    leftSpace: leftUnbounded ? null : leftSpace,
    rightSpace: rightUnbounded ? null : rightSpace,
    leftObstruction: leftUnbounded ? null : leftObstruction,
    rightObstruction: rightUnbounded ? null : rightObstruction,
    ceilingHeight,
    openingObstructions,
  };
}

module.exports = { calculateMirrorWallSpace };
